import { FC, useMemo } from 'react';
import { FREQUENCY_RATE_DISPLAY_MAP } from 'modules/planRecommender/pages/constants';
import { getText } from 'utils/stringUtil';
import {
  FIELD_DESCRIPTION_KEYS,
  MAIL_ORDER_PHARMACY_DESCRIPTION,
  RETAIL_TIER_1A_DESCRIPTION,
  RETAIL_TIER_1B_DESCRIPTION,
} from 'constants/languageConstants';
import { useAppSelector } from 'hooks/redux';
import { useMultiTableHeightSync } from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import styles from './cardTitles.module.less';

type CardTitlesProps = {
  frequency?: any;
  allowHeaderTitle: any;
  planType?: any;
  keys: any;
};

const CardTitles: FC<CardTitlesProps> = (props: CardTitlesProps) => {
  const { frequency, allowHeaderTitle, planType, keys } = props;
  const language = useAppSelector((state) => state.benguide.data?.language);

  // Collect all field names for height synchronization
  const fieldNames = useMemo(() => {
    const names: string[] = [];
    keys.forEach((titleGroup: any, titleGroupIndex: any) => {
      titleGroup.fields.forEach((title: any, titleIndex: any) => {
        if (title.fieldValue.length > 0) {
          names.push(
            title.fieldName || `title-${titleGroupIndex}-${titleIndex}`
          );
        }
      });
    });
    return names;
  }, [keys]);

  // Use multi-table height sync for all field names
  const { heights, refs } = useMultiTableHeightSync(fieldNames, 0);

  const getFeildName = (title: any) => {
    return title.fieldName === 'monthlyCost'
      ? frequency !== undefined
        ? FREQUENCY_RATE_DISPLAY_MAP[frequency]
        : title.fieldValue
      : title.fieldValue;
  };

  return (
    <div className="title-container-wrapper">
      <div
        // auto_id="plan-card-titles"
        className={`${styles.titleContainer} ${styles.titleContainerMargin}`}
      >
        {keys.map((titleGroup: any, titleGroupIndex: any) => {
          const titleGroupSize = titleGroup.length;
          const fieldList = titleGroup.fields;

          return (
            <div key={'pr-titles-sections-' + titleGroupIndex}>
              <div
                className={
                  titleGroup.sectionName
                    ? styles.titleSection
                    : styles.noTitleSection
                }
              >
                {titleGroup.sectionName}
              </div>
              {fieldList.map((title: any, titleIndex: any) => {
                const fieldName =
                  title.fieldName || `title-${titleGroupIndex}-${titleIndex}`;
                const titleHeight = heights[fieldName];
                const titleRef = refs[fieldName];

                let description = getText(
                  FIELD_DESCRIPTION_KEYS[
                    title.fieldName as keyof typeof FIELD_DESCRIPTION_KEYS
                  ],
                  language
                );
                if (title.fieldValue.includes('Mail Order Tier')) {
                  description = getText(
                    MAIL_ORDER_PHARMACY_DESCRIPTION,
                    language
                  );
                } else if (title.fieldValue.includes('1a')) {
                  description = getText(RETAIL_TIER_1A_DESCRIPTION, language);
                } else if (title.fieldValue.includes('1b')) {
                  description = getText(RETAIL_TIER_1B_DESCRIPTION, language);
                }

                if (title.fieldValue.length > 0) {
                  if (titleGroupIndex === 0 && allowHeaderTitle) {
                    if (planType === 'medical') {
                      return (
                        <div
                          key={'pr-titles-' + titleIndex}
                          className={styles.titleItem}
                          ref={titleRef}
                          style={{ height: titleHeight }}
                        >
                          {getFeildName(title) === 'Plan Name' && (
                            <div
                              className={`${styles.titleTop} ${styles.medicalPlanName}`}
                            >
                              <span className={styles.titleValue}>
                                {getFeildName(title)}
                              </span>
                              {description ? (
                                <span
                                  className={`${styles.popover} ${
                                    description.length > 50 ? 'large' : ''
                                  }`}
                                >
                                  {description}
                                </span>
                              ) : title.fieldValue.length > 20 ? (
                                <span className={styles.popover}>
                                  {title.fieldValue}
                                </span>
                              ) : (
                                <span></span>
                              )}
                            </div>
                          )}
                          {getFeildName(title) !== 'Plan Name' && (
                            <div
                              className={`${styles.titleTop} ${styles.medicalMonthlyCost}`}
                            >
                              <span className={styles.titleValue}>
                                {getFeildName(title)}
                              </span>
                              {description ? (
                                <span
                                  className={`${styles.popover} ${
                                    description.length > 50 ? styles.large : ''
                                  }`}
                                >
                                  {description}
                                </span>
                              ) : title.fieldValue.length > 20 ? (
                                <span className={styles.popover}>
                                  {title.fieldValue}
                                </span>
                              ) : (
                                <span></span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={'pr-titles-' + titleIndex}
                          className={styles.titleItem}
                          ref={titleRef}
                          style={{ height: titleHeight }}
                        >
                          <div className={styles.titleTop}>
                            <span className={styles.titleValue}>
                              {getFeildName(title)}
                            </span>
                            {description ? (
                              <span
                                className={`${styles.popover} ${
                                  description.length > 50 ? styles.large : ''
                                }`}
                              >
                                {description}
                              </span>
                            ) : title.fieldValue.length > 20 ? (
                              <span className={styles.popover}>
                                {title.fieldValue}
                              </span>
                            ) : (
                              <span></span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  } else {
                    if (titleGroupSize === titleIndex + 1) {
                      return (
                        <div
                          key={'pr-titles-' + titleIndex}
                          className={`${styles.titleItem} ${
                            titleIndex === 0 &&
                            titleGroup.sectionName &&
                            styles.titleItemWithTopBorder
                          }`}
                          ref={titleRef}
                          style={{ height: titleHeight }}
                        >
                          <div className={styles.title}>
                            <span className={styles.titleValue}>
                              {title.fieldValue}
                            </span>
                            {description ? (
                              <span
                                className={`${styles.popover} ${
                                  description.length > 50 ? 'large' : ''
                                }`}
                              >
                                {description}
                              </span>
                            ) : title.fieldValue.length > 20 ? (
                              <span className={styles.popover}>
                                {title.fieldValue}
                              </span>
                            ) : (
                              <span></span>
                            )}
                          </div>
                          <div className={styles.titleSeperator}></div>
                        </div>
                      );
                    } else {
                      return (
                        <div
                          key={'pr-titles-' + titleIndex}
                          className={`${styles.titleItem} ${
                            titleIndex === 0 &&
                            titleGroup.sectionName &&
                            styles.titleItemWithTopBorder
                          }`}
                          ref={titleRef}
                          style={{ height: titleHeight }}
                        >
                          <div className={styles.title}>
                            <span className={styles.titleValue}>
                              {title.fieldValue}
                            </span>
                            {description ? (
                              <span
                                className={`${styles.popover} ${
                                  description.length > 50 ? styles.large : ''
                                }`}
                              >
                                {description}
                              </span>
                            ) : title.fieldValue.length > 20 ? (
                              <span className={styles.popover}>
                                {title.fieldValue}
                              </span>
                            ) : (
                              <span></span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  }
                }
                return (
                  <div
                    key={'pr-titles-' + titleIndex}
                    ref={titleRef}
                    style={{ height: titleHeight }}
                  ></div>
                );
              })}
              <div className={styles.titleSeperator}></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CardTitles;
